import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { QUERY_KEYS } from '@/lib/queryKeys';
import { retryDelayByErrorKind, shouldRetryByErrorKind, throwApiErrorFromResponse } from '@/lib/errorFlow';

/**
 * Sub-menu item type
 */
export interface SubMenuItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  sort_order?: number;
  is_active?: boolean;
}

/**
 * Menu item type
 */
export interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  sort_order: number;
  is_active: boolean;
  children?: SubMenuItem[];
}

/**
 * Query key for menu/my-menu
 * @deprecated Use QUERY_KEYS.menu.myMenu instead
 */
export const MY_MENU_QUERY_KEY = QUERY_KEYS.menu.myMenu;

/**
 * Fetch user's menu items from API (filtered by permissions)
 */
const fetchMyMenu = async (): Promise<MenuItem[]> => {
  const response = await api.get<MenuItem[]>('/menu/my-menu');
  
  if (!response.ok || !response.data?.success) {
    throwApiErrorFromResponse(response, 'Không thể tải menu người dùng', 'useMyMenu:/menu/my-menu');
  }
  
  // Ensure data is an array
  const data = response.data.data;
  if (!Array.isArray(data)) {
    return [];
  }
  
  return data as MenuItem[];
};

/**
 * Custom hook to fetch user's menu items with React Query
 * 
 * Features:
 * - Cache: 2 minutes (staleTime) - data is fresh for 2 minutes
 * - Automatic refetch on window focus (to catch permission updates)
 * - Shared cache across all components using this hook
 * - Automatic deduplication (multiple components = single API call)
 * - No refetch on route change (menu items don't change per route)
 * 
 * Note: Menu items are cached and shared. They only refetch when:
 * - Data is stale (> 2 minutes)
 * - Window gains focus (refetchOnWindowFocus)
 * - Manual refetch is called
 * 
 * Usage:
 * ```tsx
 * const { data: menuItems, isLoading, error } = useMyMenu();
 * ```
 */
export const useMyMenu = () => {
  // Only fetch when token exists (prevent 401 spam on auth routes)
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  
  return useQuery({
    queryKey: QUERY_KEYS.menu.myMenu,
    queryFn: fetchMyMenu,
    enabled: !!token, // Only fetch when token exists
    staleTime: 2 * 60 * 1000, // 2 minutes - data is fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for 5 minutes
    refetchOnWindowFocus: true, // Refetch when window gains focus (to catch permission updates)
    retry: shouldRetryByErrorKind,
    retryDelay: retryDelayByErrorKind,
    // Return empty array on error (user has no permissions)
    placeholderData: [],
  });
};

